import {
  ObjectFieldValueFragment,
  RequestFormFragment,
  RequestFormType,
  RequestFormVisibility,
} from '../../../__generated__/graphql';
import { validateDateRange } from './components/utils/dateValidation';
import {
  employeeObjectApiName,
  RecordField,
  RequestFormData,
  StepType,
} from './types';

import { t } from 'i18next';

export function getAllSteps(
  requestForm: RequestFormFragment | undefined,
): StepType[] {
  const steps: StepType[] = [];

  steps.push(StepType.SelectForm);
  if (isSelectEmployeeStepVisible(requestForm)) {
    steps.push(StepType.SelectEmployee);
  }
  if (
    requestForm?.flair__Request_Type__c === RequestFormType.ObjectDataChange ||
    requestForm?.flair__Mandatory_Reason__c
  ) {
    steps.push(StepType.ReasonFiles);
  }

  if (requestForm?.flair__Request_Type__c === RequestFormType.ObjectCreate) {
    steps.push(StepType.ObjectCreate);
  }

  const positionChangeEnabled =
    requestForm?.flair__Position_Change_Enabled__c === true;
  const salaryChangeEnabled =
    requestForm?.flair__Salary_Change_Enabled__c === true;

  const employeeObjectFields =
    requestForm?.objectsFields.find(
      (field) => field.objectName === employeeObjectApiName,
    )?.fields || [];
  const hasEmployeeFields = employeeObjectFields.length > 0;

  if (positionChangeEnabled) {
    steps.push(StepType.Position);
  }
  if (salaryChangeEnabled) {
    steps.push(StepType.Salary);
  }
  if (hasEmployeeFields) {
    steps.push(StepType.EmployeeDetails);
  }

  steps.push(StepType.Review);

  return steps;
}

export function isSelectEmployeeStepVisible(
  requestForm: RequestFormFragment | undefined | null,
): boolean {
  return requestForm?.flair__Visibility__c !== RequestFormVisibility.HrEmployee;
}

export function validateOnSubmit(
  allSteps: StepType[],
  formData: RequestFormData,
): string | null {
  const hasEmployeeDetailsStep = allSteps.includes(StepType.EmployeeDetails);
  const hasValidSalaryChange =
    formData.salaryChange &&
    formData.salaryChange.formData?.value &&
    !formData.salaryChange.hasPermissionIssue;
  const hasValidPositionChange =
    formData.positionChange && formData.positionChange.newPositionId;
  const hasEmployeeDetailsChanges =
    Object.keys(formData.fields ?? {}).length > 0;

  if (
    hasEmployeeDetailsStep &&
    !hasEmployeeDetailsChanges &&
    !hasValidPositionChange &&
    !hasValidSalaryChange
  ) {
    if (formData.salaryChange?.hasPermissionIssue) {
      return t(
        'changeRequests.requestForm.errors.noChangesWithPermissionIssue',
      );
    } else {
      return t('changeRequests.requestForm.errors.noChanges');
    }
  }
  return null;
}

export function isStepValid(
  stepType: StepType,
  requestForm:
    | Pick<RequestFormFragment, 'flair__Mandatory_Reason__c'>
    | undefined,
  formData: RequestFormData,
): boolean {
  switch (stepType) {
    case StepType.SelectForm:
      return !!formData.requestFormId;
    case StepType.SelectEmployee:
      return !!formData.employeeId;
    case StepType.ReasonFiles: {
      const isReasonMandatory =
        requestForm?.flair__Mandatory_Reason__c || false;
      const isReasonValid =
        !isReasonMandatory || (formData.reason || '').trim().length > 0;
      return isReasonValid;
    }
    case StepType.Position:
      return isPositionStepValid(formData.positionChange);
    case StepType.Salary:
      return isSalaryStepValid(formData.salaryChange, formData.positionChange);
    default:
      return true;
  }
}

const isPositionStepValid = (
  positionChange: RequestFormData['positionChange'],
) => {
  if (!positionChange) return false;
  const hasSelectedNewPosition = Boolean(
    positionChange.newPositionId &&
      positionChange.newPositionId !== positionChange.currentPositionId,
  );
  const hasEffectiveDate = Boolean(
    positionChange.effectiveDate != null && positionChange.effectiveDate !== '',
  );
  const hasValidEndDate = Boolean(
    positionChange.unknownEndDate === true ||
      (positionChange.endDate != null && positionChange.endDate !== ''),
  );
  const dateValidationErrors = validateDateRange(
    positionChange.effectiveDate || '',
    positionChange.unknownEndDate ? undefined : positionChange.endDate || '',
  );
  const hasNoDateErrors =
    !dateValidationErrors.effectiveDateError &&
    !dateValidationErrors.endDateError;
  return Boolean(
    hasSelectedNewPosition &&
      hasEffectiveDate &&
      hasValidEndDate &&
      hasNoDateErrors,
  );
};

export const getEmployeeSalary = (
  employeeRecordFields: readonly ObjectFieldValueFragment[],
): number => {
  const recordFields = employeeRecordFields.map(mapToRecordField);
  const salaryField = recordFields.find(
    (field) =>
      field.fieldInfo.name.toLowerCase().includes('salary') ||
      field.fieldInfo.name.toLowerCase().includes('amount'),
  );
  return salaryField ? parseFloat(salaryField.value || '1000') : 1000;
};

export const getEmployeeDefaultValues = (
  employeeRecordFields: readonly ObjectFieldValueFragment[],
  formDataFields: RequestFormData['fields'],
): { [key: string]: string } => {
  const recordFields = employeeRecordFields.map(mapToRecordField);
  const fieldValues: { [key: string]: string } = {};
  recordFields.forEach((field) => {
    fieldValues[field.fieldInfo.name] =
      (formDataFields || {})[field.fieldInfo.name] || field.value || '';
  });
  return fieldValues;
};

const isSalaryStepValid = (
  salaryChange: RequestFormData['salaryChange'],
  positionChange: RequestFormData['positionChange'],
) => {
  if (!salaryChange) return true;
  if (salaryChange.hasPermissionIssue) return true;
  const hasAnyData =
    salaryChange.formData?.value ||
    salaryChange.formData?.effectiveDate ||
    salaryChange.formData?.selectedSalaryId;
  if (!hasAnyData) return true;
  const hasValue =
    salaryChange.formData?.value &&
    parseFloat(salaryChange.formData.value) !== 0;
  const syncDatesWithPosition = salaryChange.formData?.syncDatesWithPosition;
  const effectiveStartDate =
    syncDatesWithPosition && positionChange?.effectiveDate
      ? positionChange.effectiveDate
      : salaryChange.formData?.effectiveDate;
  const effectiveUnknownEndDate =
    syncDatesWithPosition && positionChange?.unknownEndDate !== undefined
      ? positionChange.unknownEndDate
      : salaryChange.formData?.unknownEndDate;
  const effectiveEndDate =
    syncDatesWithPosition && positionChange?.endDate
      ? positionChange.endDate
      : salaryChange.formData?.endDate;
  const hasStartDate = !!effectiveStartDate;
  const hasEndDateIfRequired = effectiveUnknownEndDate || !!effectiveEndDate;
  const salaryDateValidationErrors = validateDateRange(
    effectiveStartDate || '',
    effectiveUnknownEndDate ? undefined : effectiveEndDate || '',
  );
  const salaryHasNoDateErrors =
    !salaryDateValidationErrors.effectiveDateError &&
    !salaryDateValidationErrors.endDateError;
  if (salaryChange.formData?.updateAmountOnly) {
    const hasSelectedSalary = !!salaryChange.formData?.selectedSalaryId;
    return Boolean(
      hasSelectedSalary &&
        hasValue &&
        hasStartDate &&
        hasEndDateIfRequired &&
        salaryHasNoDateErrors,
    );
  } else {
    const hasFrequency = !!salaryChange.formData?.frequency;
    return Boolean(
      hasValue &&
        hasStartDate &&
        hasEndDateIfRequired &&
        hasFrequency &&
        salaryHasNoDateErrors,
    );
  }
};

export const mapToRecordField = (
  src: ObjectFieldValueFragment,
): RecordField => ({
  fieldInfo: {
    name: src.fieldInfo.name,
    type: src.fieldInfo.type,
    label: src.fieldInfo.label,
    disabled: false,
    required: false,
    options: src.fieldInfo.picklistValues.map((pickListItem) => ({
      label: pickListItem.label,
      value: pickListItem.value,
    })),
  },
  value: src.value,
});
